/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Overview Legacy Table
 *
 * Migrated from: lego/page/eos/overview/StatusDocuments/EosOverviewLegacyTable/EosOverviewLegacyTable.jsx
 *
 * Dual-mode table:
 *   DOWNLOAD mode:  Trial, Study Folder, Country, Site Folder, User, Download Event, Date/Time
 *   ACKNOWLEDGEMENT mode: Trial, Country, Site, WF#, WF Status, Date Initiated, Ackn Doc
 *
 * Reuses:
 *   - WorkflowDetailsModal (~/components/modals/WorkflowDetailsModal)
 *   - DateCell (~/components/shared/DateCell)
 *   - TableDateTimeCell (~/components/shared/TableDateTimeCell)
 *
 * NOTE: Legacy AckViewDocDialog has NOT been migrated to rcm-new yet.
 *       The acknowledgement document link currently opens via the service context bridge.
 *       TODO: Migrate lego/page/home/eos/AckViewDocDialog.jsx → rcm-new component
 */

import { useTranslation } from 'react-i18next'
import { useModal } from '../../../../contexts/ModalContext'
import { useServiceContext } from '../../../../root'
import WorkflowDetailsModal from '../../../../components/modals/WorkflowDetailsModal'
import DateCell from '../../../../components/shared/DateCell'
import TableDateTimeCell from '../../../../components/shared/TableDateTimeCell'
import type { EosLegacyTableProps } from '../types'
import AckViewDocDialog from '~/components/AckViewDocDialog'

const WORKFLOW_STATUS_FINISHED = 'FINISHED'

const EosOverviewLegacyTable: React.FC<EosLegacyTableProps> = ({ filesList, wasFiltered, tableViewMode }) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { showModal } = useModal()

  const getCapitalizedString = (stringValue?: string | null): string => {
    const data = stringValue?.toString() ?? ''
    return data.charAt(0).toUpperCase() + data.slice(1)
  }

  const getWfStatus = (file: (typeof filesList)[number]): string => {
    let translationKey = 'NOT_AVAILABLE'

    if (file.acknowledgement?.workflowStatus) {
      translationKey = `WORKFLOWS_STEP_STATUS_${file.acknowledgement.workflowStatus}`
      if (WORKFLOW_STATUS_FINISHED === file.acknowledgement.workflowStatus) {
        translationKey = 'STATE_APPROVED'
      }
    }

    return t(translationKey)
  }

  /**
   * Open acknowledgement document viewer.
   * NOTE: AckViewDocDialog has not yet been migrated to rcm-new.
   * Using service bridge for PDF URL + a simple window.open as interim.
   */
  const openAckDocument = async (file: (typeof filesList)[number]) => {
    const ack = file.acknowledgement
    if (!ack?.acknowledgementDocumentId) return

    showModal(
      <AckViewDocDialog
        pdfSrc={{
          service: () => {
            return (context.api as any).files?.getPdfUrlInShowFile(
              file?.acknowledgement?.acknowledgementDocumentId,
              -1,
              0
            )
          }
        }}
        pdfInfo={{
          fsiId: file?.acknowledgement?.acknowledgementDocumentId
        }}
        fileName={file?.acknowledgement?.acknowledgementDocumentName}
        isEosOverviewFlow
      />
    )
  }

  return (
    <div>
      <table className="styled-table chita-table mb0 EosOverviewLegacyTable">
        <thead>
          {tableViewMode === 'DOWNLOAD' && (
            <tr>
              <th>{t('EMTF_MD_TRIAL')}</th>
              <th>{t('TABLE_TH_STUDY_FOLDER_NAME')}</th>
              <th>{t('EMTF_MD_COUNTRY')}</th>
              <th>{t('TABLE_TH_SITE_FOLDER_NAME')}</th>
              <th>{t('REPORTS_TABLE_TH_USER')}</th>
              <th>{t('TABLE_TH_DOWNLOAD_EVENT')}</th>
              <th>{t('REPORTS_TABLE_TH_DATETIME')}</th>
            </tr>
          )}
          {tableViewMode === 'ACKNOWLEDGEMENT' && (
            <tr>
              <th>{t('EMTF_MD_TRIAL')}</th>
              <th>{t('EMTF_MD_COUNTRY')}</th>
              <th>{t('EMTF_MD_SITE')}</th>
              <th>{t('REPORTS_TABLE_TH_WF_NO')}</th>
              <th>{t('TABLE_TH_WORKFLOW_STATUS')}</th>
              <th>{t('TABLE_TH_DATE_INITIATED')}</th>
              <th>{t('TABLE_TH_ACKN_DOC')}</th>
            </tr>
          )}
        </thead>

        {(!filesList || filesList.length === 0) && (
          <tbody>
            <tr className="no-results">
              <td colSpan={9}>{t('NO_RESULTS_FILTERED')}</td>
            </tr>
          </tbody>
        )}

        <tbody>
          {filesList?.map((file, index) => (
            <tr key={index}>
              {tableViewMode === 'DOWNLOAD' && (
                <>
                  <td>{file.studyName ?? t('NOT_AVAILABLE')}</td>
                  <td>{file.studyFolderName ?? t('NOT_AVAILABLE')}</td>
                  <td>{file.country ?? t('NOT_AVAILABLE')}</td>
                  <td>{file.siteFolderName ?? t('NOT_AVAILABLE')}</td>
                  <td>{file.download?.user ?? t('NOT_AVAILABLE')}</td>
                  <td>
                    {file.download?.downloadEvent
                      ? getCapitalizedString(file.download.downloadEvent)
                      : t('NOT_AVAILABLE')}
                  </td>
                  <td>
                    <span>
                      {file.download?.downloadedAt ? (
                        <TableDateTimeCell val={file.download.downloadedAt} />
                      ) : (
                        t('NOT_AVAILABLE')
                      )}
                    </span>
                  </td>
                </>
              )}

              {tableViewMode === 'ACKNOWLEDGEMENT' && (
                <>
                  <td>{file.studyName ?? t('NOT_AVAILABLE')}</td>
                  <td>{file.country ?? t('NOT_AVAILABLE')}</td>
                  <td>{file.siteFolderName ?? t('NOT_AVAILABLE')}</td>
                  <td>
                    {file.acknowledgement?.workflowFormattedId ? (
                      <a
                        className="text-link"
                        onClick={() => showModal(<WorkflowDetailsModal wfId={file.acknowledgement!.workflowId!} />)}
                      >
                        {file.acknowledgement.workflowFormattedId}
                      </a>
                    ) : (
                      t('NOT_AVAILABLE')
                    )}
                  </td>
                  <td>{getWfStatus(file)}</td>
                  <td>
                    {file.acknowledgement?.dateInitiated ? (
                      <DateCell val={file.acknowledgement.dateInitiated} />
                    ) : (
                      t('NOT_AVAILABLE')
                    )}
                  </td>
                  <td>
                    {file.acknowledgement?.acknowledgementDocumentId ? (
                      <a className="text-link" onClick={() => openAckDocument(file)}>
                        {file.acknowledgement.acknowledgementDocumentName}
                      </a>
                    ) : (
                      <span>{file.acknowledgement?.acknowledgementDocumentName ?? t('NOT_AVAILABLE')}</span>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default EosOverviewLegacyTable
